import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { Trip } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import SelectBar from "@app/component/view/SelectBar";
import DeleteReason, {
  getDeleteReasonDisplayName,
} from "@app/scene/reports/components/MileageTracking/enum/DeleteReason";
import { deleteTrips } from "@app/scene/reports/store/actions";
import { areDeletingTrips } from "@app/scene/reports/store/mileageTracking/selectors";
import { RootState } from "@app/store/rootReducer";

import DeleteConfirmation from "./DeleteConfirmation";
import styles from "./TripSelectBar.module.scss";

export interface TripSelectBarProps {
  onReloadData: () => void;
  selectedTrips: Set<Trip>;
}

interface Props extends TripSelectBarProps {
  deleting: boolean;
  onDeleteTrips: (payload: {
    onSuccess?: () => void;
    reason: string;
    tripIds: Trip["id"][];
  }) => void;
}

const TripSelectBar: FC<Props> = ({ deleting, onDeleteTrips, onReloadData, selectedTrips }) => {
  const intl = useIntl();

  const [deleteConfirmationVisible, showDeleteConfirmation, hideDeleteConfirmation] = useBoolean();

  const removableTrips = useMemo(
    () => Array.from(selectedTrips).filter((trip) => trip.accessStatus?.delete),
    [selectedTrips]
  );

  const handleConfirmDelete = useCallback(
    (deleteReason: DeleteReason) => {
      hideDeleteConfirmation();

      if (removableTrips.length) {
        onDeleteTrips({
          onSuccess: () => {
            onReloadData();
          },
          reason: getDeleteReasonDisplayName(intl, deleteReason),
          tripIds: removableTrips.map(({ id }) => id),
        });
      }
    },
    [hideDeleteConfirmation, intl, onDeleteTrips, onReloadData, removableTrips]
  );

  if (!selectedTrips.size) {
    return null;
  }

  return (
    <>
      <SelectBar
        className={styles.container}
        entityType={EntityType.TRIP}
        extraActions={
          <Button
            danger
            disabled={deleting || !removableTrips.length}
            loading={deleting}
            onClick={showDeleteConfirmation}
            type="default"
          >
            {intl.formatMessage(
              {
                id: "reports.mileageTracking.selectBar.delete",
                defaultMessage: "Delete {count, plural, one {Drive} other {Drives}}",
                description: "Delete button on a selected entities bar",
              },
              {
                count: selectedTrips.size,
              }
            )}
          </Button>
        }
        onReloadData={onReloadData}
        selectedEntities={selectedTrips}
        showAddRemoveButton={false}
        showDataViewButton={false}
        showDeleteButton={false}
        showEmailButton={false}
        visible
      />
      {deleteConfirmationVisible && (
        <DeleteConfirmation
          count={selectedTrips.size}
          onCancel={hideDeleteConfirmation}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  deleting: areDeletingTrips(state),
});

const mapDispatchToProps = {
  onDeleteTrips: deleteTrips.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TripSelectBar);
